import React, { useCallback, useEffect, useState, useMemo } from "react";
import { AxisDomain } from "recharts/types/util/types";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceArea,
} from "recharts";
import { actions } from "@shared/store";
import { CategoricalChartState } from "recharts/types/chart/generateCategoricalChart";
import { SensorDataFile, SensorDataFileRow, SensorDataSessionMarkerDot, Station } from "@shared/models";
import { Option, SensorDataSessionValuesFilter } from "@shared/interfaces";
import { Loader, MultiToggle } from "@shared/components";
import { SensorDataSessionProcessedValues, SensorDataSessionValues } from "@containers/SensorDataSession/interfaces";
import { getChartPrevNextTime } from "@shared/utils";
import { AUTOPLAY_INTERVAL, MODAL_TYPES } from "@shared/constants";
import { useDispatch } from "react-redux";

import { CustomDot, CustomLegend } from "./components";
import { CustomTooltip } from "./components/CustomTooltip";

import "./index.scss";

export interface SensorDataSessionChartProps {
  intervalOptions: Option<number>[];
  elements: SensorDataFile[] | Partial<Station>[];
  savedDots?: SensorDataSessionMarkerDot[];
  minTime: Date | string | null | undefined;
  maxTime: Date | string | null | undefined;
  values: SensorDataSessionValues[];
  processedValues?: SensorDataSessionProcessedValues[];
  filter: SensorDataSessionValuesFilter | null;
  loading: boolean;
  domain?: AxisDomain;
  isPrevNextButtonsVisible?: boolean;
  isChartWithConfigVisible?: boolean;
  autoPlayInterval?: number;
  onChangeFilter: (filter: Partial<SensorDataSessionValuesFilter>) => void;
  onSaveDot?: (dot: any) => void;
  combineValues: (values: SensorDataSessionValues[], dots: SensorDataSessionMarkerDot[]) => SensorDataFileRow[];
  handleIntervalChange: (value: Option<number>) => void;
  onChangeActiveDot?: (payload: { seconds: number; station_code: string }) => void;
  isAutoPlay?: boolean;
}

const SensorDataSessionChart: React.FC<SensorDataSessionChartProps> = (props) => {
  const {
    values,
    filter,
    onChangeFilter,
    elements,
    loading,
    minTime,
    maxTime,
    onSaveDot,
    combineValues,
    handleIntervalChange,
    intervalOptions,
    onChangeActiveDot,
    domain,
    isPrevNextButtonsVisible = true,
    isChartWithConfigVisible = true,
    autoPlayInterval = AUTOPLAY_INTERVAL,
    savedDots = [],
    processedValues = [],
  } = props;
  const dispatch = useDispatch();

  const [isFirstIteration, setIsFirstIteration] = useState(true);
  const [nextTime, setNextTime] = useState<{ start?: string; end?: string }>({ start: undefined, end: undefined });
  const [prevTime, setPrevTime] = useState<{ start?: string; end?: string }>({ start: undefined, end: undefined });

  const [refAreaLeft, setRefAreaLeft] = useState<SensorDataFileRow | null>(null);
  const [refAreaRight, setRefAreaRight] = useState<SensorDataFileRow | null>(null);
  const [isAutoNextPage, setIsAutoNextPage] = useState(true);
  const [prevValues, setPrevValues] = useState<SensorDataSessionValues[] | null>(values);

  const combinedValues = useMemo(() => {
    return combineValues(values, savedDots);
  }, [combineValues, values, savedDots]);

  const reversedSelectedElements = useMemo(() => {
    if (!elements) return elements;
    return [...elements].reverse();
  }, [elements]);

  const chartMouseDown = useCallback((item: CategoricalChartState) => {
    if (item?.activePayload?.length) {
      const activePayload = item.activePayload.find(Boolean);
      setRefAreaLeft(activePayload.payload);
    }
  }, []);

  const chartMouseMove = useCallback(
    (item: CategoricalChartState) => {
      if (item.activeLabel && refAreaLeft && item?.activePayload?.length) {
        const activePayload = item.activePayload.find(Boolean);
        setRefAreaRight(activePayload.payload);
      }
    },
    [refAreaLeft],
  );

  const chartMouseUp = useCallback(() => {
    setRefAreaRight(null);
    setRefAreaLeft(null);
  }, []);

  const getPrevItems = useCallback(() => {
    setIsFirstIteration(false);
    onChangeFilter(prevTime);
  }, [onChangeFilter, prevTime]);

  const getNextItems = useCallback(() => {
    if (nextTime.start) {
      setIsFirstIteration(false);
      onChangeFilter(nextTime);
    }
  }, [onChangeFilter, nextTime]);

  const handlePauseResumeStep = useCallback(() => {
    setPrevValues(values);
    if (!isAutoNextPage) {
      getNextItems();
    }

    setIsAutoNextPage(!isAutoNextPage);
  }, [isAutoNextPage, getNextItems, values]);

  useEffect(() => {
    if (filter && elements?.length && filter.start && filter.end && filter.interval && minTime && maxTime) {
      const { next, prev } = getChartPrevNextTime(filter, minTime, maxTime);
      setNextTime(next);
      setPrevTime(prev);
    }
  }, [filter, elements, minTime, maxTime]);

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;

    if (isAutoNextPage && !loading && elements.length) {
      interval = setTimeout(() => {
        getNextItems();
      }, autoPlayInterval);
    }

    return () => {
      clearInterval(interval);
    };
  }, [isAutoNextPage, getNextItems, values, loading, elements, autoPlayInterval]);

  useEffect(() => {
    const rows = values.map((value) => value.rows).flat();
    if (isAutoNextPage && values !== prevValues && !rows.length) {
      setPrevValues(values);
      setIsAutoNextPage(false);

      dispatch(
        actions.showModal({
          type: MODAL_TYPES.INFO,
          props: {
            heading: "No Data Available",
            content:
              "The graph has paused due to a lack of new data from the database. Please wait for new data to populate",
            cancelBtnText: "Cancel",
          },
        }),
      );
    }
  }, [dispatch, isAutoNextPage, combinedValues, values, prevValues]);

  return reversedSelectedElements?.length ? (
    <div className="sensor-data-session-chart-container">
      <div className="pagination-wrapper">
        <div className="auto-next" onClick={handlePauseResumeStep}>
          {isAutoNextPage ? "Pause" : "Resume"}
        </div>
        {isPrevNextButtonsVisible ? (
          <>
            <button disabled={!prevTime.end || isFirstIteration || filter?.start === minTime} onClick={getPrevItems}>
              <i className="fa fa-arrow-left" aria-hidden="true" />
            </button>
            <button disabled={!nextTime.start} onClick={getNextItems}>
              <i className="fa fa-arrow-right" aria-hidden="true" />
            </button>
          </>
        ) : null}
      </div>

      {loading ? <Loader className="chart-loader" /> : null}
      <div className="chart-container">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            onMouseDown={chartMouseDown}
            onMouseMove={chartMouseMove}
            onMouseUp={chartMouseUp}
            data={combinedValues}
            width={500}
            height={300}
          >
            <CartesianGrid />
            <XAxis dataKey="time" domain={domain} />
            <Tooltip
              content={
                <CustomTooltip
                  refAreaLeft={refAreaLeft}
                  refAreaRight={refAreaRight}
                  processedValues={processedValues}
                  onChangeActiveDot={onChangeActiveDot}
                />
              }
            />
            <Legend content={<CustomLegend items={elements} />} verticalAlign="top" />
            {elements.map((element, i) => (
              <YAxis
                key={element.id || (element as Station).station_code}
                dataKey={element.id || (element as Station).station_code}
                yAxisId={element.id || (element as Station).station_code}
                orientation={i % 2 === 0 ? "left" : "right"}
              />
            ))}
            {reversedSelectedElements.map((element) => (
              <Line
                yAxisId={element.id || (element as Station).station_code}
                key={element.id || (element as Station).station_code}
                dataKey={element.id || (element as Station).station_code}
                stroke={element.color}
                isAnimationActive={false}
                type="monotone"
                dot={<CustomDot />}
                activeDot={{
                  className: "active-dot",
                  onClick: (_, dotInfo) => onSaveDot?.(dotInfo),
                  r: 5,
                }}
              />
            ))}

            {refAreaLeft && refAreaRight && reversedSelectedElements.length ? (
              <ReferenceArea
                yAxisId={reversedSelectedElements[0].id || (reversedSelectedElements[0] as Station).station_code}
                x1={refAreaLeft.time}
                x2={refAreaRight.time}
                strokeOpacity={0.3}
              />
            ) : null}
          </LineChart>
        </ResponsiveContainer>
      </div>
      {isChartWithConfigVisible ? (
        <div className="chart-settings">
          <div className="interval">
            <MultiToggle
              options={intervalOptions}
              value={filter?.interval}
              labelClassName="interval-label"
              labelTitle="Graph width"
              onChange={handleIntervalChange}
            />
          </div>
        </div>
      ) : null}
    </div>
  ) : (
    <div className="empty-chart-box">No Graph to display</div>
  );
};

export default SensorDataSessionChart;
